// ============================================================================
// EmployeeApp — Interfaccia dipendente ottimizzata per smartphone
// ============================================================================

import { useState, useEffect } from "react";
import { LogOut, Clock, Check, ChevronDown, ChevronUp, AlertTriangle, X, Bell, BellOff } from "lucide-react";
import { AVATAR_COLORS, ORE_OPTIONS, STATUS_COLORS } from "../../lib/constants";
import { formatDate, todayStr } from "../../lib/helpers";
import { useAppSettings } from "../../hooks/useOreData";
import {
  isPushSupported, isStandalone, isIos,
  getPermissionState, getCurrentSubscription,
  subscribeForDipendente,
} from "../../lib/pushNotifications";

const STATI_DIPENDENTE = [];

const TRANSLATIONS = {
  it: {
    dipendente: "Dipendente",
    settimana: "Settimana",
    oggi: "Oggi",
    inserisciOre: "Inserisci Ore",
    data: "Data",
    cantiere: "Cantiere",
    oreLavorate: "Ore lavorate",
    orePersonalizzate: "Ore personalizzate (es. 3.5)",
    note: "Note (opzionale)",
    notePlaceholder: "Es. straordinario, lavori urgenti...",
    selezionaCantiere: "Seleziona cantiere...",
    salva: "Salva",
    aggiornaRegistrazione: "Aggiorna registrazione",
    salvato: "Salvato!",
    registrazioneCompletata: "Registrazione completata",
    oreGiaRegistrate: "Ore già registrate per oggi",
    haiInserito: "Hai già inserito",
    su: "su",
    modificaRegistrazione: "Modifica registrazione",
    registrazioneBloccata: "Registrazione bloccata",
    contattoAdmin: "Contatta l'amministratore per modifiche.",
    haiInseritoPer: "Hai già inserito",
    perQuestoGiorno: "per questo giorno.",
    selezionaUnCantiere: "Seleziona un cantiere",
    inserisciOreLavorate: "Inserisci le ore lavorate",
    storicoInserimenti: "Storico inserimenti",
    nascondiStorico: "Nascondi storico",
    oreSalvateLocale: "Ore salvate in locale",
    invioAutomatico: "Verranno inviate automaticamente quando torni online",
    errore: "Errore",
    hoCapito: "Ho capito",
    avvisoImportante: "Avviso Importante",
    avvisiImportanti: "Avvisi Importanti",
    leggiAttentamente: "Leggi attentamente prima di procedere",
    visitaMedica: "Visita medica",
    formazione: "Formazione",
    visitaFissata: "Visita fissata per il",
    formazioneFissata: "Formazione fissata per il",
    orario: "Orario",
    oggiExcl: "OGGI!",
    tra: "Tra",
    giorno: "giorno",
    giorni: "giorni",
    pushTitle: "Attiva avvisi visite mediche",
    pushDesc: "Ricevi una notifica sul telefono quando viene programmata una visita.",
    pushEnable: "Attiva notifiche",
    pushEnabling: "Attivazione…",
    pushEnabled: "Notifiche attive ✓",
    pushDenied: "Permesso notifiche negato. Abilitalo dalle impostazioni del telefono.",
    pushIosInstall: "Su iPhone: tocca Condividi e poi “Aggiungi alla schermata Home”, poi riapri da lì.",
    pushUnsupported: "Le notifiche non sono supportate su questo dispositivo/browser.",
    pushError: "Errore attivazione notifiche",
  },
  ur: {
    dipendente: "ملازم",
    settimana: "ہفتہ",
    oggi: "آج",
    inserisciOre: "اوقات درج کریں",
    data: "تاریخ",
    cantiere: "کام کی جگہ",
    oreLavorate: "کام کے گھنٹے",
    orePersonalizzate: "حسب ضرورت گھنٹے (مثال 3.5)",
    note: "نوٹ (اختیاری)",
    notePlaceholder: "مثال: اوور ٹائم، فوری کام...",
    selezionaCantiere: "...کام کی جگہ منتخب کریں",
    salva: "محفوظ کریں",
    aggiornaRegistrazione: "اندراج کو اپ ڈیٹ کریں",
    salvato: "!محفوظ ہو گیا",
    registrazioneCompletata: "اندراج مکمل",
    oreGiaRegistrate: "آج کے گھنٹے پہلے سے درج ہیں",
    haiInserito: "آپ نے پہلے سے درج کیا ہے",
    su: "پر",
    modificaRegistrazione: "اندراج میں ترمیم کریں",
    registrazioneBloccata: "اندراج مقفل ہے",
    contattoAdmin: "تبدیلیوں کے لیے ایڈمن سے رابطہ کریں۔",
    haiInseritoPer: "آپ نے پہلے سے درج کیا ہے",
    perQuestoGiorno: "اس دن کے لیے۔",
    selezionaUnCantiere: "کام کی جگہ منتخب کریں",
    inserisciOreLavorate: "کام کے گھنٹے درج کریں",
    storicoInserimenti: "پچھلے اندراجات",
    nascondiStorico: "تاریخ چھپائیں",
    oreSalvateLocale: "گھنٹے مقامی طور پر محفوظ",
    invioAutomatico: "آن لائن ہونے پر خود بخود بھیجے جائیں گے",
    errore: "خرابی",
    hoCapito: "سمجھ گیا ✓",
    avvisoImportante: "اہم اطلاع",
    avvisiImportanti: "اہم اطلاعات",
    leggiAttentamente: "آگے بڑھنے سے پہلے غور سے پڑھیں",
    visitaMedica: "طبی معائنہ",
    formazione: "تربیت",
    visitaFissata: "طبی معائنہ مقرر ہے",
    formazioneFissata: "تربیت مقرر ہے",
    orario: "وقت",
    oggiExcl: "!آج",
    tra: "میں",
    giorno: "دن",
    giorni: "دن",
    pushTitle: "طبی معائنہ کے اطلاعات فعال کریں",
    pushDesc: "جب کوئی طبی معائنہ مقرر ہو تو فون پر اطلاع موصول کریں۔",
    pushEnable: "اطلاعات فعال کریں",
    pushEnabling: "...فعال ہو رہا ہے",
    pushEnabled: "✓ اطلاعات فعال ہیں",
    pushDenied: "اطلاعات کی اجازت مسترد۔ فون کی ترتیبات سے اجازت دیں۔",
    pushIosInstall: "آئی فون پر: شیئر کو دبائیں اور \"ہوم اسکرین میں شامل کریں\"، پھر وہاں سے کھولیں۔",
    pushUnsupported: "اس آلے/براؤزر پر اطلاعات کی سہولت دستیاب نہیں۔",
    pushError: "اطلاعات فعال کرنے میں خرابی",
  },
  en: {
    dipendente: "Employee",
    settimana: "Week",
    oggi: "Today",
    inserisciOre: "Enter Hours",
    data: "Date",
    cantiere: "Job site",
    oreLavorate: "Hours worked",
    orePersonalizzate: "Custom hours (e.g. 3.5)",
    note: "Notes (optional)",
    notePlaceholder: "E.g. overtime, urgent work...",
    selezionaCantiere: "Select job site...",
    salva: "Save",
    aggiornaRegistrazione: "Update entry",
    salvato: "Saved!",
    registrazioneCompletata: "Entry completed",
    oreGiaRegistrate: "Hours already entered for today",
    haiInserito: "You already entered",
    su: "on",
    modificaRegistrazione: "Edit entry",
    registrazioneBloccata: "Entry locked",
    contattoAdmin: "Contact your administrator for changes.",
    haiInseritoPer: "You already entered",
    perQuestoGiorno: "for this day.",
    selezionaUnCantiere: "Select a job site",
    inserisciOreLavorate: "Enter hours worked",
    storicoInserimenti: "Entry history",
    nascondiStorico: "Hide history",
    oreSalvateLocale: "Hours saved locally",
    invioAutomatico: "Will be sent automatically when you're back online",
    errore: "Error",
    hoCapito: "Got it ✓",
    avvisoImportante: "Important Notice",
    avvisiImportanti: "Important Notices",
    leggiAttentamente: "Read carefully before proceeding",
    visitaMedica: "Medical checkup",
    formazione: "Training",
    visitaFissata: "Checkup scheduled for",
    formazioneFissata: "Training scheduled for",
    orario: "Time",
    oggiExcl: "TODAY!",
    tra: "In",
    giorno: "day",
    giorni: "days",
    pushTitle: "Enable medical checkup alerts",
    pushDesc: "Receive a phone notification when a checkup is scheduled.",
    pushEnable: "Enable notifications",
    pushEnabling: "Enabling…",
    pushEnabled: "Notifications enabled ✓",
    pushDenied: "Notification permission denied. Enable it from your phone settings.",
    pushIosInstall: "On iPhone: tap Share and \"Add to Home Screen\", then open from there.",
    pushUnsupported: "Notifications are not supported on this device/browser.",
    pushError: "Error enabling notifications",
  },
};

export default function EmployeeApp({ user, onLogout, oreData, addOre, cantieri, refreshOreData, promemoria = [], hideLogout = false }) {
  const [visiteProgrammate] = useAppSettings("visiteProgrammate", {});
  const [formazioneProgrammate] = useAppSettings("formazioneProgrammate", {});
  const [lang, setLang] = useState(() => localStorage.getItem("empLang") || "it");
  const t = TRANSLATIONS[lang] || TRANSLATIONS.it;
  const langOrder = ["it", "en", "ur"];
  const langLabels = { it: "IT", en: "EN", ur: "اردو" };
  const toggleLang = () => { const idx = langOrder.indexOf(lang); const next = langOrder[(idx + 1) % langOrder.length]; setLang(next); localStorage.setItem("empLang", next); };
  const [cantiere, setCantiere] = useState("");
  const [ore, setOre] = useState("8");
  const [oreCustom, setOreCustom] = useState("");
  const [stato, setStato] = useState("");
  const [note, setNote] = useState("");
  const [data, setData] = useState(todayStr());
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Promemoria — modal bloccante
  const [reminderDismissed, setReminderDismissed] = useState(() => {
    return localStorage.getItem(`reminder_ack_${user.id}`) === todayStr();
  });
  const activeReminders = promemoria.filter((r) => {
    if (Number(r.dipendente_id) !== Number(user.id)) return false;
    const scad = new Date(r.data_scadenza);
    const start = new Date(scad);
    start.setDate(start.getDate() - (r.giorni_preavviso || 2));
    const oggi = new Date(todayStr());
    return oggi >= start && oggi <= scad;
  });
  const showReminderModal = activeReminders.length > 0 && !reminderDismissed;
  function handleDismissReminders() {
    localStorage.setItem(`reminder_ack_${user.id}`, todayStr());
    setReminderDismissed(true);
  }

  // Push notifications — stato iscrizione corrente del dispositivo
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState("default"); // default | granted | denied | unsupported
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [pushDismissed, setPushDismissed] = useState(() => localStorage.getItem(`push_banner_dismissed_${user.id}`) === "1");
  const iosNeedsInstall = isIos() && !isStandalone();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const supported = isPushSupported();
      if (!mounted) return;
      setPushSupported(supported);
      if (!supported) { setPushPermission("unsupported"); return; }
      setPushPermission(await getPermissionState());
      const sub = await getCurrentSubscription();
      if (!mounted) return;
      setPushSubscribed(!!sub);
    })();
    return () => { mounted = false; };
  }, []);

  async function handleEnablePush() {
    setPushError(null);
    setPushBusy(true);
    try {
      await subscribeForDipendente(user.id);
      setPushPermission("granted");
      setPushSubscribed(true);
    } catch (err) {
      setPushError(err?.message || t.pushError);
      const p = await getPermissionState();
      setPushPermission(p);
    } finally {
      setPushBusy(false);
    }
  }

  function dismissPushBanner() {
    localStorage.setItem(`push_banner_dismissed_${user.id}`, "1");
    setPushDismissed(true);
  }

  // Banner visibile solo se: supportato, NON già attivo, NON dismissed
  // Su iOS in browser (non standalone) mostriamo l'invito a installare
  const showPushBanner =
    !pushDismissed &&
    pushSupported &&
    !(pushPermission === "granted" && pushSubscribed);

  // Avvisi imminenti — modal combinato visita + formazione (entro 15 giorni)
  // Non persistiamo il dismiss: ricompare ad ogni apertura dell'app
  const [avvisiDismissed, setAvvisiDismissed] = useState(false);
  const avvisiImminenti = (() => {
    const list = [];
    const checkItem = (source, tipo, emoji, colore) => {
      const entry = source[user.id];
      if (!entry?.data) return;
      const dt = new Date(entry.data);
      const diff = Math.round((dt - new Date()) / (1000 * 60 * 60 * 24));
      // Mostra solo da 15 giorni prima fino al giorno stesso (incluso); dopo sparisce
      if (diff > 15 || diff < 0) return;
      list.push({ tipo, emoji, colore, dt, diff, ora: entry.ora || null });
    };
    checkItem(visiteProgrammate, "visita", "🏥", "#06b6d4");
    checkItem(formazioneProgrammate, "formazione", "📋", "#f59e0b");
    return list;
  })();
  const showAvvisiModal = avvisiImminenti.length > 0 && !avvisiDismissed && !showReminderModal;
  function handleDismissAvvisi() {
    setAvvisiDismissed(true);
  }
  const myOre = oreData
    .filter((o) => o.dipendenteId === user.id)
    .sort((a, b) => (b.data > a.data ? 1 : b.data < a.data ? -1 : 0));

  const todayEntry = myOre.find((o) => o.data === todayStr());
  const weekOre = myOre
    .filter((o) => { const d = new Date(o.data); const w = new Date(); w.setDate(w.getDate() - 7); return d >= w; })
    .reduce((s, o) => s + o.ore, 0);

  const oreEffettive = () => {
    if (oreCustom.trim() !== "") return parseFloat(oreCustom) || 0;
    if (stato && ore === "8") return 0;
    return parseFloat(ore);
  };

  const canSubmit = cantiere && (oreCustom.trim() !== "" || ore !== "");
  const existingForDate = myOre.find((o) => o.data === data);
  const canEditExisting = existingForDate && existingForDate.data === todayStr();
  const isLocked = existingForDate && existingForDate.data !== todayStr();

  const handleModifica = () => {
    setCantiere(existingForDate.cantiere || "");
    setOre(String(existingForDate.ore || "8"));
    setOreCustom("");
    setStato(existingForDate.stato || "");
    setNote(existingForDate.note || "");
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaveError(null);
    const result = await addOre({
      dipendenteId: user.id, dipendente: user.name,
      cantiere: cantiere || "", ore: oreEffettive(),
      stato: stato || null, data, note,
    });
    if (result.success) {
      if (result.offline) {
        setSaveError(null);
        setSaved(true);
        // Messaggio speciale per salvataggio offline — usiamo saveError con prefisso speciale
        setTimeout(() => {
          setSaved(false);
          setSaveError("OFFLINE");
          setCantiere(""); setOre("8"); setOreCustom(""); setStato(""); setNote("");
        }, 1500);
      } else {
        setSaved(true);
        setTimeout(() => {
          setSaved(false);
          setCantiere(""); setOre("8"); setOreCustom(""); setStato(""); setNote("");
        }, 2500);
      }
    } else {
      setSaveError(result.error || "Errore durante il salvataggio. Riprova.");
    }
  };

  const colorIdx = (user.id - 1) % AVATAR_COLORS.length;

  return (
    <div style={{
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at 20% 0%, rgba(192,57,43,0.18) 0%, transparent 55%),
        radial-gradient(ellipse at 80% 100%, rgba(150,40,27,0.14) 0%, transparent 55%),
        var(--bg)
      `,
      display: "flex", flexDirection: "column", position: "relative", overflow: "hidden",
    }}>
      {/* Modal Promemoria — blocca l'app finché non si preme OK */}
      {showReminderModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "var(--surface)", borderRadius: 20, width: "100%", maxWidth: 400, border: "2px solid #f59e0b", boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 40px rgba(245,158,11,0.2)", padding: "28px 24px" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>🔔</div>
              <h2 style={{ color: "var(--text)", fontSize: 20, fontWeight: 800 }}>Hai {activeReminders.length === 1 ? "un promemoria" : "dei promemoria"}!</h2>
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6 }}>Leggi prima di inserire le ore</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {activeReminders.map((r) => {
                const scad = new Date(r.data_scadenza);
                const diff = Math.ceil((scad - new Date(todayStr())) / (1000 * 60 * 60 * 24));
                return (
                  <div key={r.id} style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 12, padding: "14px 16px" }}>
                    <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 700 }}>{r.testo}</p>
                    <p style={{ color: "#f59e0b", fontSize: 13, marginTop: 5, fontWeight: 600 }}>
                      📅 {scad.toLocaleDateString("it-IT")} — {diff === 0 ? "Oggi!" : diff === 1 ? "Domani" : `Tra ${diff} giorni`}
                    </p>
                  </div>
                );
              })}
            </div>
            <button onClick={handleDismissReminders} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: "var(--font)", boxShadow: "0 4px 20px rgba(245,158,11,0.4)" }}>
              {t.hoCapito}
            </button>
          </div>
        </div>
      )}

      {/* MEGA BANNER — Avvisi imminenti (visita + formazione) — blocca lo schermo */}
      {showAvvisiModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "var(--surface)", borderRadius: 24, width: "100%", maxWidth: 440, border: `3px solid ${avvisiImminenti[0]?.colore || "#C0392B"}`, boxShadow: `0 30px 100px rgba(0,0,0,0.8), 0 0 60px ${avvisiImminenti[0]?.colore || "#C0392B"}25`, padding: "36px 28px" }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 64, marginBottom: 14, lineHeight: 1 }}>{avvisiImminenti.length === 1 ? avvisiImminenti[0].emoji : "📅"}</div>
              <h2 style={{ color: "var(--text)", fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>
                {avvisiImminenti.length === 1 ? t.avvisoImportante : t.avvisiImportanti}
              </h2>
              <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 8 }}>{t.leggiAttentamente}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 }}>
              {avvisiImminenti.map((a) => (
                <div key={a.tipo} style={{ background: `${a.colore}15`, border: `2px solid ${a.colore}55`, borderRadius: 16, padding: "22px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 32 }}>{a.emoji}</span>
                    <p style={{ color: a.colore, fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
                      {a.tipo === "visita" ? t.visitaMedica : t.formazione}
                    </p>
                  </div>
                  <p style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                    {a.tipo === "visita" ? t.visitaFissata : t.formazioneFissata} <span style={{ color: a.colore }}>{a.dt.toLocaleDateString(lang === "ur" ? "ur-PK" : lang === "en" ? "en-GB" : "it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                  </p>
                  {a.ora && (
                    <p style={{ color: "var(--text)", fontSize: 16, marginTop: 8, fontWeight: 600 }}>
                      {t.orario}: <span style={{ color: a.colore }}>{a.ora}</span>
                    </p>
                  )}
                  <div style={{ marginTop: 12, background: `${a.colore}20`, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                    <p style={{ color: a.colore, fontSize: 16, fontWeight: 800 }}>
                      {a.diff === 0 ? t.oggiExcl : `${t.tra} ${a.diff} ${a.diff === 1 ? t.giorno : t.giorni}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleDismissAvvisi} style={{ width: "100%", padding: "18px", background: `linear-gradient(135deg, ${avvisiImminenti[0]?.colore || "#C0392B"}, ${avvisiImminenti[0]?.colore || "#C0392B"}cc)`, color: "#fff", border: "none", borderRadius: 14, fontSize: 18, fontWeight: 800, cursor: "pointer", fontFamily: "var(--font)", boxShadow: `0 6px 30px ${avvisiImminenti[0]?.colore || "#C0392B"}50`, letterSpacing: 0.5 }}>
              {t.hoCapito}
            </button>
          </div>
        </div>
      )}

      {/* Cerchi decorativi sfondo */}
      <div style={{ position: "fixed", top: -120, left: -120, width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(192,57,43,0.08)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", bottom: -100, right: -100, width: 420, height: 420, borderRadius: "50%", border: "1px solid rgba(192,57,43,0.08)", pointerEvents: "none", zIndex: 0 }} />

      {/* Header sticky */}
      <div style={{
        background: "rgba(17,24,39,0.9)", backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(192,57,43,0.25)",
        padding: "12px 16px",
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 20,
        boxShadow: "0 4px 24px rgba(192,57,43,0.1)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: `linear-gradient(135deg, ${AVATAR_COLORS[colorIdx][0]}, ${AVATAR_COLORS[colorIdx][1]})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 15, color: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}>{user.avatar}</div>
          <div>
            <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{user.name}</p>
            <p style={{ color: "var(--text-dim)", fontSize: 11, marginTop: 1 }}>{t.dipendente}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={toggleLang} style={{
            background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)",
            borderRadius: 10, padding: "10px 12px", color: "#60a5fa",
            cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "var(--font)", minHeight: 44,
          }}>
            {langLabels[langOrder[(langOrder.indexOf(lang) + 1) % langOrder.length]]}
          </button>
          {!hideLogout && (
            <button onClick={onLogout} style={{
              background: "rgba(192,57,43,0.1)", border: "1px solid rgba(192,57,43,0.25)",
              borderRadius: 10, padding: "10px 14px", color: "var(--text-muted)",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              fontSize: 13, fontFamily: "var(--font)", minHeight: 44,
            }}>
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Contenuto scrollabile */}
      <div style={{ flex: 1, padding: "16px 16px 32px", maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box", position: "relative", zIndex: 1 }}>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          <div style={{
            background: "linear-gradient(145deg, rgba(192,57,43,0.1), rgba(150,40,27,0.05))",
            borderRadius: 16, padding: "16px 14px",
            border: "1px solid rgba(192,57,43,0.2)",
          }}>
            <p style={{ color: "var(--text-dim)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>{t.settimana}</p>
            <p style={{ color: "var(--text)", fontSize: 32, fontWeight: 800, lineHeight: 1 }}>
              {weekOre}<span style={{ fontSize: 14, color: "var(--text-muted)", fontWeight: 400 }}>h</span>
            </p>
          </div>
          <div style={{
            background: todayEntry ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
            borderRadius: 16, padding: "16px 14px",
            border: `1px solid ${todayEntry ? "rgba(16,185,129,0.25)" : "rgba(245,158,11,0.25)"}`,
          }}>
            <p style={{ color: "var(--text-dim)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>{t.oggi}</p>
            <p style={{ color: todayEntry ? "var(--accent)" : "var(--warning)", fontSize: 32, fontWeight: 800, lineHeight: 1 }}>
              {todayEntry
                ? (todayEntry.stato
                    ? <span style={{ fontSize: 16, background: STATUS_COLORS[todayEntry.stato]?.bg, color: STATUS_COLORS[todayEntry.stato]?.text, borderRadius: 6, padding: "3px 8px" }}>{todayEntry.stato}</span>
                    : `${todayEntry.ore}h`)
                : "—"}
            </p>
          </div>
        </div>

        {/* Banner attivazione notifiche push — sopra agli avvisi */}
        {showPushBanner && (
          <div style={{
            background: "linear-gradient(145deg, rgba(96,165,250,0.12), rgba(59,130,246,0.06))",
            border: "1px solid rgba(96,165,250,0.35)",
            borderRadius: 16,
            padding: "14px 16px",
            marginBottom: 16,
            display: "flex", alignItems: "flex-start", gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: "rgba(96,165,250,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Bell size={20} color="#60a5fa" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: "var(--text)", fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
                {t.pushTitle}
              </p>
              <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                {iosNeedsInstall ? t.pushIosInstall : t.pushDesc}
              </p>
              {pushError && (
                <p style={{ color: "#ef4444", fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                  {pushError}
                </p>
              )}
              {pushPermission === "denied" && !iosNeedsInstall && (
                <p style={{ color: "#f59e0b", fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                  {t.pushDenied}
                </p>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {!iosNeedsInstall && pushPermission !== "denied" && (
                  <button
                    onClick={handleEnablePush}
                    disabled={pushBusy}
                    style={{
                      padding: "10px 14px", borderRadius: 10, border: "none",
                      background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
                      color: "#fff", fontWeight: 700, fontSize: 13, cursor: pushBusy ? "default" : "pointer",
                      fontFamily: "var(--font)", opacity: pushBusy ? 0.7 : 1,
                      boxShadow: "0 4px 16px rgba(59,130,246,0.35)",
                    }}>
                    {pushBusy ? t.pushEnabling : t.pushEnable}
                  </button>
                )}
                <button
                  onClick={dismissPushBanner}
                  style={{
                    padding: "10px 12px", borderRadius: 10,
                    background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                    color: "var(--text-muted)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font)",
                  }}>
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Banner avvisi persistente — sempre visibile se ci sono avvisi */}
        {avvisiImminenti.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {avvisiImminenti.map((a) => (
              <div key={a.tipo} style={{
                background: `${a.colore}15`,
                border: `2px solid ${a.colore}55`,
                borderRadius: 16,
                padding: "16px 18px",
                display: "flex", alignItems: "center", gap: 14,
              }}>
                <span style={{ fontSize: 32, lineHeight: 1 }}>{a.emoji}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: a.colore, fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {a.tipo === "visita" ? t.visitaMedica : t.formazione}
                  </p>
                  <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 700, marginTop: 3 }}>
                    {a.dt.toLocaleDateString(lang === "ur" ? "ur-PK" : lang === "en" ? "en-GB" : "it-IT", { weekday: "short", day: "numeric", month: "long" })}{a.ora ? ` — ${t.orario} ${a.ora}` : ""}
                  </p>
                  <p style={{ color: a.colore, fontSize: 13, fontWeight: 700, marginTop: 2 }}>
                    {a.diff === 0 ? t.oggiExcl : `${t.tra} ${a.diff} ${a.diff === 1 ? t.giorno : t.giorni}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form / Success */}
        {saved ? (
          <div className="animate-scale-in" style={{
            background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: 20, padding: "56px 24px", textAlign: "center",
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%", background: "var(--accent)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", boxShadow: "0 8px 32px rgba(16,185,129,0.4)",
            }}><Check size={36} color="#fff" strokeWidth={2.5} /></div>
            <p style={{ color: "var(--text)", fontSize: 24, fontWeight: 800 }}>{t.salvato}</p>
            <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 8 }}>{t.registrazioneCompletata}</p>
          </div>
        ) : (
          <div style={{
            background: "linear-gradient(145deg, rgba(192,57,43,0.08), rgba(150,40,27,0.04))",
            borderRadius: 20, padding: "20px 16px",
            border: "1px solid rgba(192,57,43,0.2)",
            boxShadow: "0 8px 32px rgba(192,57,43,0.08)",
            overflow: "hidden",
          }}>
            <h2 style={{ color: "var(--text)", fontSize: 17, fontWeight: 700, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={18} color="#C0392B" /> {t.inserisciOre}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Data */}
              <div style={{ overflow: "hidden" }}>
                <label style={labelStyle}>{t.data}</label>
                <input type="date" value={data} onChange={(e) => setData(e.target.value)}
                  style={{ ...inputStyle, textAlign: "left", display: "block", maxWidth: "100%", overflow: "hidden", WebkitAppearance: "none", appearance: "none" }} />
              </div>

              {/* Cantiere */}
              <div>
                <label style={labelStyle}>{t.cantiere} *</label>
                <select value={cantiere} onChange={(e) => setCantiere(e.target.value)}
                  style={{ ...inputStyle, color: cantiere ? "var(--text)" : "var(--text-dim)" }}>
                  <option value="">{t.selezionaCantiere}</option>
                  {cantieri.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Ore — bottoni griglia */}
              <div>
                <label style={labelStyle}>{t.oreLavorate} *</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 10 }}>
                  {["0", ...ORE_OPTIONS].map((h) => {
                    const active = ore === h && oreCustom === "";
                    return (
                      <button key={h} onClick={() => { setOre(h); setOreCustom(""); }}
                        style={{
                          padding: "14px 8px", borderRadius: 12, border: "none",
                          background: active ? "var(--primary)" : "var(--surface-light)",
                          color: active ? "#fff" : "var(--text-muted)",
                          fontWeight: 700, fontSize: 16, cursor: "pointer",
                          transition: "all 0.15s",
                          boxShadow: active ? "0 4px 16px var(--primary-glow)" : "none",
                          fontFamily: "var(--font)", minHeight: 52,
                        }}
                      >{h}h</button>
                    );
                  })}
                </div>
                <input
                  type="number" inputMode="decimal" min="0" max="24" step="0.5"
                  placeholder={t.orePersonalizzate}
                  value={oreCustom}
                  onChange={(e) => { setOreCustom(e.target.value); setOre(""); }}
                  style={inputStyle}
                />
              </div>

              {/* Note */}
              <div>
                <label style={labelStyle}>{t.note}</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={t.notePlaceholder}
                  rows={2}
                  style={{ ...inputStyle, resize: "none", fontFamily: "var(--font)" }}
                />
              </div>

              {/* Avviso modificabile */}
              {canEditExisting && (
                <div style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 12, padding: "14px" }}>
                  <p style={{ color: "var(--warning)", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>⚠️ {t.oreGiaRegistrate}</p>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
                    {t.haiInserito} {existingForDate.ore}h{existingForDate.cantiere ? ` ${t.su} ${existingForDate.cantiere}` : ""}.
                  </p>
                  <button onClick={handleModifica} style={{
                    width: "100%", padding: "13px", borderRadius: 10, minHeight: 48,
                    border: "1px solid rgba(245,158,11,0.5)", background: "rgba(245,158,11,0.15)",
                    color: "var(--warning)", fontWeight: 700, fontSize: 15,
                    cursor: "pointer", fontFamily: "var(--font)",
                  }}>✏️ {t.modificaRegistrazione}</button>
                </div>
              )}

              {/* Bloccato */}
              {isLocked && (
                <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: "14px", display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>🔒</span>
                  <div>
                    <p style={{ color: "#ef4444", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t.registrazioneBloccata}</p>
                    <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
                      {t.haiInseritoPer} {existingForDate.ore}h {t.perQuestoGiorno} {t.contattoAdmin}
                    </p>
                  </div>
                </div>
              )}

              {/* Validazione */}
              {!canSubmit && !isLocked && (
                <p style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center" }}>
                  {!cantiere ? `⚠️ ${t.selezionaUnCantiere}` : `⚠️ ${t.inserisciOreLavorate}`}
                </p>
              )}

              {/* Errore salvataggio / Banner offline */}
              {saveError && saveError === "OFFLINE" ? (
                <div style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 12, padding: "14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Clock size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p style={{ color: "#f59e0b", fontSize: 14, fontWeight: 700 }}>{t.oreSalvateLocale}</p>
                    <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{t.invioAutomatico}</p>
                  </div>
                </div>
              ) : saveError ? (
                <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 12, padding: "14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <AlertTriangle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <p style={{ color: "#ef4444", fontSize: 14, fontWeight: 600 }}>{t.errore}: {saveError}</p>
                </div>
              ) : null}

              {/* Submit */}
              {!isLocked && (
                <button onClick={handleSubmit} disabled={!canSubmit} style={{
                  width: "100%", padding: "18px", borderRadius: 14, border: "none",
                  minHeight: 56,
                  background: canSubmit
                    ? "linear-gradient(135deg, #C0392B, #96281B)"
                    : "var(--surface-light)",
                  color: canSubmit ? "#fff" : "var(--text-dim)",
                  fontWeight: 800, fontSize: 17,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                  boxShadow: canSubmit ? "0 8px 32px rgba(192,57,43,0.35)" : "none",
                  fontFamily: "var(--font)",
                  letterSpacing: "0.3px",
                }}>
                  {canEditExisting ? `✓ ${t.aggiornaRegistrazione}` : `✓ ${t.salva}`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Storico */}
        <button
          onClick={() => { const next = !showHistory; setShowHistory(next); if (next && refreshOreData) refreshOreData(); }}
          style={{
            width: "100%", marginTop: 14, padding: "16px", minHeight: 52,
            background: "linear-gradient(145deg, rgba(192,57,43,0.08), rgba(150,40,27,0.04))",
            border: "1px solid rgba(192,57,43,0.2)",
            borderRadius: 14, color: "var(--text-muted)", cursor: "pointer",
            fontSize: 14, fontWeight: 600, fontFamily: "var(--font)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showHistory ? t.nascondiStorico : `${t.storicoInserimenti} (${myOre.length})`}
        </button>

        {showHistory && (
          <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {myOre.slice(0, 15).map((o) => (
              <div key={o.id} style={{
                background: "linear-gradient(145deg, rgba(192,57,43,0.06), rgba(150,40,27,0.03))",
                borderRadius: 12,
                padding: "14px 16px", border: "1px solid rgba(192,57,43,0.15)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <p style={{ color: "var(--text)", fontSize: 14, fontWeight: 600 }}>{formatDate(o.data)}</p>
                  <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 3 }}>
                    {o.cantiere ? o.cantiere.split(" - ")[0] : (o.stato ? STATUS_COLORS[o.stato]?.label : "—")}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {o.stato && (
                    <span style={{
                      background: STATUS_COLORS[o.stato]?.bg, color: STATUS_COLORS[o.stato]?.text,
                      borderRadius: 8, padding: "4px 10px", fontWeight: 700, fontSize: 12,
                    }}>{o.stato}</span>
                  )}
                  {o.ore > 0 && (
                    <span style={{
                      background: "rgba(192,57,43,0.12)", color: "#C0392B",
                      borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 15,
                    }}>{o.ore}h</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  color: "var(--text-muted)", fontSize: 13, fontWeight: 600,
  display: "block", marginBottom: 8, letterSpacing: "0.2px",
};

const inputStyle = {
  width: "100%", padding: "15px 16px",
  background: "var(--surface-light)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  color: "var(--text)", fontSize: 16,
  outline: "none", boxSizing: "border-box",
  fontFamily: "var(--font)",
  minHeight: 52,
};
